import { useState, type FormEvent } from 'react';
import { Check, Pencil, Plus, Settings2, Sparkles, Trash2, Wrench } from 'lucide-react';
import { AsyncView, Button, Card, Chip, Dialog, Input, Notice, Table, Textarea, useToast } from '../ui';
import { Switch } from '../ui/Switch';
import { Kebab } from '../ui/Kebab';
import { useResource } from '../lib/useResource';
import { podeAtuar, useSessao } from '../lib/session';
import { api, ApiError } from '../lib/api';
import { FormularioAgente, MENSAGENS_PADRAO } from './FormularioAgente';
import { AgentContactMemories } from './AgentContactMemories';
import './agentes-ia.css';

import type { AgenteIA } from './FormularioAgente';
interface Memoria { key: string; content: string; expiresAt: string | null }
interface Trilha { id:string; intent:string|null; agentKey:string|null; action:string; confidence:number|null; reason:string|null; status:string; durationMs:number|null; createdAt:string; contact:{profileName:string|null;waId:string}; handoffs:Array<{fromAgentKey:string|null;toAgentKey:string;reason:string}>; tools:Array<{toolId:string;status:string;risk:string;durationMs:number|null;failed:boolean}> }

/** Iniciais a partir do nome: duas letras, sem preposição no meio. */
function iniciais(nome: string): string {
  const partes = nome.trim().split(/\s+/).filter((p) => !/^(de|da|do|das|dos|e)$/i.test(p));
  const primeira = partes[0]?.[0] ?? '?';
  const ultima = partes.length > 1 ? partes.at(-1)![0] : (partes[0]?.[1] ?? '');
  return (primeira + ultima).toUpperCase();
}

/** Tom estável por chave: o mesmo agente tem sempre a mesma cor, em qualquer
 *  sessão e em qualquer ordem de listagem. Aleatório seria inútil como pista. */
function tom(chave: string): number {
  let soma = 0;
  for (const letra of chave) soma = (soma + letra.charCodeAt(0)) % 997;
  return soma % 5;
}

function CartaoAgente({ agente, podeEditar, onAlternar, onConfigurar, onExcluir, salvando }: {
  agente: AgenteIA;
  podeEditar: boolean;
  onAlternar: (a: AgenteIA) => void;
  onConfigurar: (a: AgenteIA) => void;
  onExcluir: (a: AgenteIA) => void;
  salvando: boolean;
}) {
  const entrada = agente.isEntryPoint;
  return (
    <article className="agente" data-off={!agente.enabled}>
      <div className="agente__capa" data-tom={tom(agente.key)} aria-hidden="true">
        {/* Iniciais continuam sendo o padrão: a maioria dos agentes não terá
            foto, e um placeholder genérico faria todos parecerem iguais. */}
        {agente.hasAvatar
          ? <img className="agente__foto" src={`/api/ai-agents/${agente.key}/avatar`} alt="" />
          : iniciais(agente.name)}
      </div>

      <div className="agente__corpo">
        <div>
          <div className="agente__titulo">
            <h3 className="agente__nome">{agente.name}</h3>
            {/* Ponto de entrada como chip com texto, não como selo.
                O selo dizia "este é especial" sem dizer o quê — e a informação
                aqui é operacional: é este agente que recebe toda mensagem
                nova. Quem não conhece o ícone não tinha como descobrir. */}
            {entrada && <Chip tone="brand">Ponto de entrada</Chip>}
          </div>
          <span className="agente__chave">{agente.key}</span>
        </div>

        <p className="agente__bio">{agente.description}</p>

        <div className="agente__bloco">
          <span className="agente__rotulo" id={`hab-${agente.key}`}>Habilidades</span>
          {agente.capabilities.length ? (
            <div className="agente__lista" role="list" aria-labelledby={`hab-${agente.key}`}>
              {agente.capabilities.map((c) => <span role="listitem" key={c}><Chip kind="tag">{c}</Chip></span>)}
            </div>
          ) : (
            <span className="agente__vazio">Nenhuma habilidade declarada.</span>
          )}
        </div>

        <div className="agente__bloco">
          <span className="agente__rotulo" id={`fer-${agente.key}`}>Ferramentas</span>
          {agente.tools.length ? (
            <div className="agente__lista" role="list" aria-labelledby={`fer-${agente.key}`}>
              {agente.tools.map((t) => <span role="listitem" key={t}><Chip kind="tag">{t}</Chip></span>)}
            </div>
          ) : (
            /* Ferramenta é o que dá ao modelo acesso a dado real do cliente.
               "Nenhuma" é informação de segurança, não falta de conteúdo. */
            <span className="agente__vazio">Sem acesso a ferramentas.</span>
          )}
        </div>
      </div>

      <div className="agente__rodape">
        {/* Os dois números repetem o que os chips acima já mostram, de
            propósito: nos chips a informação é qual; aqui é quanto. Um agente
            com dez ferramentas e outro com uma se distinguem à distância. */}
        <div className="agente__numeros">
          <span className="agente__numero">
            <Sparkles size={16} aria-hidden="true" />
            <strong>{agente.capabilities.length}</strong>
            <span className="sr-only">habilidades</span>
          </span>
          <span className="agente__numero">
            <Wrench size={16} aria-hidden="true" />
            <strong>{agente.tools.length}</strong>
            <span className="sr-only">ferramentas</span>
          </span>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
        {/* Só o ícone: o rótulo "Configurar" repetido em cada card competia
            com o nome do agente pela atenção, e a ação é a mesma em todos. O
            nome acessível continua completo e cita o agente. */}
        {podeEditar && (
          <button
            type="button"
            className="agente__config"
            onClick={() => onConfigurar(agente)}
            aria-label={`Configurar ${agente.name}`}
          >
            <Settings2 size={18} aria-hidden="true" />
          </button>
        )}
        {/* Excluir só existe para quem PODE ser excluído: o servidor recusa o
            ponto de entrada com "o agente de entrada não pode ser excluído", e
            renderizar um botão que sempre falha é a mesma armadilha do
            interruptor ao lado, que já é bloqueado pelo mesmo motivo. Dá para
            apagar um ponto de entrada — basta antes eleger outro. */}
        {podeEditar && !entrada && (
          <button
            type="button"
            className="agente__config agente__config--perigo"
            onClick={() => onExcluir(agente)}
            aria-label={`Excluir ${agente.name}`}
          >
            <Trash2 size={18} aria-hidden="true" />
          </button>
        )}
        {podeEditar ? (
          <Switch
            checked={agente.enabled}
            onChange={() => onAlternar(agente)}
            label={`Agente ${agente.name}`}
            estadoTexto={{ ligado: 'Ligado', desligado: 'Desligado' }}
            /* O servidor recusa desligar o ponto de entrada. Bloquear aqui
               evita um erro que a pessoa não tinha como prever. */
            disabled={salvando || (entrada && agente.enabled)}
            motivo={entrada && agente.enabled ? 'o ponto de entrada precisa permanecer ativo' : undefined}
          />
        ) : (
          <Chip tone={agente.enabled ? 'success' : 'neutral'}>{agente.enabled ? 'Ligado' : 'Desligado'}</Chip>
        )}
        </div>
      </div>
    </article>
  );
}

export function AgentesIA() {
  const { agent } = useSessao();
  const toast = useToast();
  const podeVer = podeAtuar(agent.role, 'ADMIN');

  const agentes = useResource<AgenteIA[]>('/ai-agents', {
    enabled: podeVer,
    disabledReason: 'Agentes de IA acessam dados do workspace e são visíveis apenas para administradores.',
  });
  const memorias = useResource<Memoria[]>('/ai-agents/memories/organization', { enabled: podeVer });
  const trilhas = useResource<Trilha[]>('/ai-agents/traces?take=30', { enabled: podeVer });

  const semEntrada = agentes.state.status === 'ready' && !agentes.state.data.some((a) => a.isEntryPoint && a.enabled);
  const [salvando, setSalvando] = useState<string | null>(null);
  const [form, setForm] = useState<{ aberto: boolean; agente: AgenteIA | null }>({ aberto: false, agente: null });
  const [excluindoAgente, setExcluindoAgente] = useState<AgenteIA | null>(null);
  const [apagandoAgente, setApagandoAgente] = useState(false);

  async function excluirAgente() {
    if (!excluindoAgente) return;
    setApagandoAgente(true);
    try {
      await api(`/ai-agents/${excluindoAgente.key}`, { method: 'DELETE' });
      toast(`"${excluindoAgente.name}" foi excluído.`);
      setExcluindoAgente(null);
      agentes.recarregar();
    } catch (erro) {
      toast(erro instanceof ApiError ? erro.message : 'Falha ao excluir.', 'danger');
    } finally {
      setApagandoAgente(false);
    }
  }

  async function alternar(a: AgenteIA) {
    setSalvando(a.key);
    try {
      // PATCH /ai-agents/:key valida a definição inteira, não um diff.
      await api(`/ai-agents/${a.key}`, {
        method: 'PATCH',
        body: {
          // `avatarUrl` fica FORA: omitir mantém a foto atual. Enviar
          // `null` aqui apagaria a imagem só por ligar/desligar o agente.
          name: a.name, description: a.description, enabled: !a.enabled,
          isEntryPoint: a.isEntryPoint, intents: a.intents, tools: a.tools,
          capabilities: a.capabilities, systemPrompt: a.systemPrompt,
          messages: a.messages ?? MENSAGENS_PADRAO,
        },
      });
      agentes.recarregar();
      toast(a.enabled ? `"${a.name}" desligado.` : `"${a.name}" ligado.`);
    } catch (erro) {
      toast(erro instanceof ApiError ? erro.message : 'Não foi possível alterar o agente.', 'danger');
    } finally {
      setSalvando(null);
    }
  }

  return (
    <div style={{ display: 'grid', gap: 'var(--space-6)' }}>
      {semEntrada && (
        <Notice tone="warning" title="Sem ponto de entrada ativo">
          Nenhum agente ligado está marcado como ponto de entrada. Mensagens novas não serão roteadas para nenhum agente.
        </Notice>
      )}

      <div className="agentes__topo">
        <p style={{ margin: 0, fontSize: 'var(--text-sm)', color: 'var(--text-muted)' }}>
          Agentes respondem e classificam conversas antes de um operador assumir.
        </p>
        {podeVer && (
          <Button
            variant="primary"
            icon={<Plus size={18} aria-hidden="true" />}
            onClick={() => setForm({ aberto: true, agente: null })}
          >
            Novo agente
          </Button>
        )}
      </div>

      <AsyncView
        state={agentes.state}
        label="os agentes"
        skeletonRows={2}
        empty={{
          title: 'Nenhum agente configurado',
          text: 'Agentes de IA respondem e classificam conversas antes de um operador assumir.',
          action: podeVer ? (
            <Button variant="secondary" icon={<Plus size={18} aria-hidden="true" />} onClick={() => setForm({ aberto: true, agente: null })}>
              Novo agente
            </Button>
          ) : undefined,
        }}
      >
        {(linhas) => (
          <div className="agentes">
            {linhas.map((a) => (
              <CartaoAgente
                key={a.key}
                agente={a}
                podeEditar={podeVer}
                salvando={salvando === a.key}
                onAlternar={(x) => void alternar(x)}
                onConfigurar={(x) => setForm({ aberto: true, agente: x })}
                onExcluir={(x) => setExcluindoAgente(x)}
              />
            ))}
          </div>
        )}
      </AsyncView>

      <FormularioAgente
        aberto={form.aberto}
        agente={form.agente}
        onFechar={() => setForm({ aberto: false, agente: null })}
        onSalvo={agentes.recarregar}
      />

      <Dialog
        open={excluindoAgente !== null}
        onClose={() => setExcluindoAgente(null)}
        title="Confirmar exclusão"
        footer={
          <>
            <Button variant="secondary" onClick={() => setExcluindoAgente(null)}>Cancelar</Button>
            <Button variant="danger" loading={apagandoAgente} icon={<Trash2 size={18} aria-hidden="true" />} onClick={() => void excluirAgente()}>
              Excluir definitivamente
            </Button>
          </>
        }
      >
        <p>
          {excluindoAgente
            ? `O agente "${excluindoAgente.name}" e a configuração dele serão apagados. Automações que encaminham para ele param de responder, e conversas em andamento com ele voltam para a fila humana.`
            : ''}
        </p>
      </Dialog>

      <MemoriaOrganizacional
        state={memorias.state}
        recarregar={memorias.recarregar}
        podeEditar={podeVer}
      />
      <AgentContactMemories enabled={podeVer} />

      <Card title="Rastreabilidade recente" subtitle="Decisões, encaminhamentos e ferramentas usados pelo orquestrador." as="h2">
        <AsyncView state={trilhas.state} label="as trilhas de agentes" skeletonRows={3} empty={{title:'Nenhuma execução registrada',text:'As próximas mensagens processadas pelo orquestrador aparecerão aqui.'}}>
          {(linhas) => (
            <Table caption="Execuções recentes do orquestrador" rowKey={(linha)=>linha.id} rows={linhas} columns={[
            {key:'contato',header:'Contato',cell:(linha)=><div><strong>{linha.contact.profileName ?? linha.contact.waId}</strong><br/><small>{linha.contact.waId}</small></div>},
            {key:'decisao',header:'Decisão',cell:(linha)=><div><strong>{linha.agentKey ?? 'Sem agente'}</strong><br/><small>{linha.intent ?? linha.action}</small></div>},
            {key:'execucao',header:'Execução',cell:(linha)=><div className="agentes__trace-tags"><Chip tone={linha.status==='COMPLETED'?'success':'neutral'}>{linha.status}</Chip>{linha.tools.map((tool)=><Chip key={`${linha.id}-${tool.toolId}`} tone={tool.failed?'danger':'neutral'}>{tool.toolId}</Chip>)}</div>},
            {key:'motivo',header:'Motivo',cell:(linha)=>linha.reason ?? '—'},
            {key:'data',header:'Data',cell:(linha)=>new Intl.DateTimeFormat('pt-BR',{dateStyle:'short',timeStyle:'short'}).format(new Date(linha.createdAt))},
            ]}/>
          )}
        </AsyncView>
      </Card>
    </div>
  );
}

/**
 * Memória da organização — agora com escrita.
 *
 * `PUT /memories/organization` é upsert por chave: criar e editar são a mesma
 * chamada. A diferença fica na interface — ao editar, a chave vira somente
 * leitura, porque alterá-la não renomeia a memória, cria outra e deixa a antiga
 * para trás. Deixar o campo editável faria "renomear" apagar dado em silêncio.
 */
function MemoriaOrganizacional({ state, recarregar, podeEditar }: {
  state: Parameters<typeof AsyncView<Memoria[]>>[0]['state'];
  recarregar: () => void;
  podeEditar: boolean;
}) {
  const toast = useToast();
  const [aberto, setAberto] = useState(false);
  const [editando, setEditando] = useState<Memoria | null>(null);
  const [excluindo, setExcluindo] = useState<Memoria | null>(null);
  const [enviando, setEnviando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  function abrirNova() { setEditando(null); setErro(null); setAberto(true); }
  function abrirEdicao(m: Memoria) { setEditando(m); setErro(null); setAberto(true); }

  async function salvar(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    const chave = editando ? editando.key : String(form.get('key') ?? '').trim();
    const conteudo = String(form.get('content') ?? '').trim();
    const validade = String(form.get('expiresAt') ?? '').trim();
    setEnviando(true);
    setErro(null);
    try {
      await api('/ai-agents/memories/organization', {
        method: 'PUT',
        body: {
          key: chave,
          content: conteudo,
          // O schema aceita ISO ou null. Um <input type="date"> devolve
          // "2026-09-01", que não é datetime válido — daí o T00:00 e o fuso.
          expiresAt: validade ? new Date(`${validade}T23:59:59`).toISOString() : null,
        },
      });
      setAberto(false);
      recarregar();
      toast(editando ? 'Memória atualizada.' : 'Memória criada.');
    } catch (falha) {
      setErro(falha instanceof ApiError ? falha.message : 'Falha de conexão.');
    } finally {
      setEnviando(false);
    }
  }

  async function excluir() {
    if (!excluindo) return;
    setEnviando(true);
    try {
      await api(`/ai-agents/memories/organization/${encodeURIComponent(excluindo.key)}`, { method: 'DELETE' });
      setExcluindo(null);
      recarregar();
      toast('Memória removida.');
    } catch (falha) {
      setExcluindo(null);
      toast(falha instanceof ApiError ? falha.message : 'Não foi possível remover.', 'danger');
    } finally {
      setEnviando(false);
    }
  }

  return (
    <>
      <Card
        title="Memória da organização"
        subtitle="Fatos que todos os agentes consultam, independente do contato."
        as="h2"
        actions={podeEditar
          ? <Button variant="primary" icon={<Plus size={18} aria-hidden="true" />} onClick={abrirNova}>Nova memória</Button>
          : undefined}
      >
        <AsyncView
          state={state}
          label="as memórias"
          skeletonRows={2}
          empty={{
            title: 'Nenhuma memória registrada',
            text: 'Memórias guardam informação estável do negócio — horário de atendimento, política de troca — para os agentes não precisarem perguntar.',
            action: podeEditar
              ? <Button variant="secondary" icon={<Plus size={18} aria-hidden="true" />} onClick={abrirNova}>Nova memória</Button>
              : undefined,
          }}
        >
          {(linhas) => (
            <Table
              caption="Memórias da organização"
              rowKey={(m) => m.key}
              rows={linhas}
              columns={[
                { key: 'chave', header: 'Chave', cell: (m) => <code style={{ fontSize: 'var(--text-xs)' }}>{m.key}</code> },
                { key: 'conteudo', header: 'Conteúdo', cell: (m) => m.content },
                { key: 'validade', header: 'Validade', cell: (m) => (
                  m.expiresAt
                    ? new Date(m.expiresAt).toLocaleDateString('pt-BR')
                    : <span style={{ color: 'var(--text-muted)' }}>Permanente</span>
                ) },
                ...(podeEditar ? [{
                  key: '__acoes', header: 'Ações', align: 'end' as const,
                  // Mesmo menu das outras listagens: duas ações por linha em
                  // botões soltos davam ao "Excluir" o mesmo peso do "Editar".
                  cell: (m: Memoria) => (
                    <Kebab
                      rotulo={m.key}
                      acoes={[
                        { rotulo: 'Editar', icone: <Pencil size={16} aria-hidden="true" />, onSelect: () => abrirEdicao(m) },
                        { rotulo: 'Remover', icone: <Trash2 size={16} aria-hidden="true" />, perigo: true, separar: true, onSelect: () => setExcluindo(m) },
                      ]}
                    />
                  ),
                }] : []),
              ]}
            />
          )}
        </AsyncView>
      </Card>

      <Dialog
        open={aberto}
        onClose={() => setAberto(false)}
        title={editando ? 'Editar memória' : 'Nova memória'}
        description={editando ? undefined : 'A chave identifica o fato e não muda depois de criada.'}
        footer={
          <>
            <Button variant="secondary" onClick={() => setAberto(false)}>Cancelar</Button>
            <Button variant="primary" type="submit" form="form-memoria" loading={enviando} icon={<Check size={18} aria-hidden="true" />}>Salvar</Button>
          </>
        }
      >
        <form
          key={editando?.key ?? 'nova'}
          id="form-memoria"
          onSubmit={salvar}
          style={{ display: 'grid', gap: 'var(--space-4)' }}
        >
          {erro && <p className="field__error" role="alert">{erro}</p>}
          <Input
            label="Chave"
            name="key"
            required
            readOnly={editando !== null}
            defaultValue={editando?.key}
            placeholder="horario_atendimento"
            hint={editando
              ? 'A chave não pode ser alterada: trocá-la criaria outra memória e deixaria esta para trás.'
              : 'Sem espaços. Use minúsculas e sublinhado.'}
          />
          <Textarea
            label="Conteúdo"
            name="content"
            required
            rows={4}
            defaultValue={editando?.content}
            hint="Escreva como você explicaria a um colega novo. Até 4000 caracteres."
          />
          <Input
            label="Validade"
            name="expiresAt"
            type="date"
            defaultValue={editando?.expiresAt ? editando.expiresAt.slice(0, 10) : undefined}
            hint="Em branco: permanente. Com data: os agentes param de considerar este fato depois dela."
          />
        </form>
      </Dialog>

      <Dialog
        open={excluindo !== null}
        onClose={() => setExcluindo(null)}
        title="Remover memória"
        footer={
          <>
            <Button variant="secondary" onClick={() => setExcluindo(null)}>Cancelar</Button>
            <Button variant="danger" onClick={() => void excluir()} loading={enviando} icon={<Trash2 size={18} aria-hidden="true" />}>Remover</Button>
          </>
        }
      >
        <p>
          {excluindo
            ? `A memória "${excluindo.key}" deixa de ser consultada pelos agentes. Respostas que dependiam dela mudam a partir da próxima conversa.`
            : ''}
        </p>
      </Dialog>
    </>
  );
}
